import React, { useEffect, useState } from 'react';
import { Building2, MapPin, Phone, Edit, Check, Trash2, Tag, Briefcase, Mail, User, DollarSign, Layers, Accessibility } from 'lucide-react';
import { ClientService } from '../services/clientService';
import { ContactService } from '../services/contactService';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, ConfirmDialog, Checkbox, Badge } from './ui';
import ContactDetailsModal from './ContactDetailsModal';

const ClientDetailsModal = ({ client, isOpen, onClose, initialEditMode = false, onClientUpdated, canManageClients = true }) => {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [formData, setFormData] = useState({
    name: '',
    clientStatus: 'Active Client',
    clientOf: '',
    leadSource: '',
    servicesProvided: [],
    brand: [],
    trimbleFormats: [],
    address: '',
    state: '',
    companyPhone: '',
    reason: '',
    levelOfDetail: '',
    needsHandicapRamps: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  const fieldOptions = ClientService.getFieldOptions();

  // Convert field options to dropdown format
  const clientStatusOptions = fieldOptions.clientStatus.map(status => ({
    value: status,
    label: status
  }));

  const leadSourceOptions = [
    { value: '', label: 'None' },
    ...fieldOptions.leadSource.map(source => ({
      value: source,
      label: source
    }))
  ];

  const levelOfDetailOptions = [
    { value: '', label: 'None' },
    ...fieldOptions.levelOfDetail.map(level => ({
      value: level,
      label: level
    }))
  ];

  const clientOfOptions = [
    { value: '', label: 'None' },
    { value: 'AB Civil', label: 'AB Civil' },
    { value: 'AB Civil NC', label: 'AB Civil NC' }
  ];

  // Clean array data that might have PostgreSQL array formatting issues
  const cleanArrayValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      // Clean each item in the array to remove PostgreSQL formatting artifacts
      return value.map(item => {
        if (typeof item === 'string') {
          // Aggressively remove all leading/trailing quotes, braces, and brackets
          let cleaned = item;
          // Keep removing quotes, braces, and brackets until none are left
          while (/^[{["']|[}\]"']$/.test(cleaned)) {
            cleaned = cleaned.replace(/^[{["']+|[}\]"']+$/g, '');
          }
          return cleaned.trim();
        }
        return item;
      }).filter(item => item && item !== 'None' && item !== 'null');
    }
    // If it's a string, try to parse it
    if (typeof value === 'string') {
      // Remove outer PostgreSQL array brackets
      let cleaned = value.replace(/^\{|\}$/g, '').replace(/^\[|\]$/g, '');
      // Split by comma and clean each item
      return cleaned.split(',').map(item => {
        // Aggressively remove all leading/trailing quotes, braces, and brackets
        let cleanedItem = item;
        while (/^[{["']|[}\]"']$/.test(cleanedItem)) {
          cleanedItem = cleanedItem.replace(/^[{["']+|[}\]"']+$/g, '');
        }
        return cleanedItem.trim();
      }).filter(item => item && item !== 'None' && item !== 'null');
    }
    return [];
  };

  // Initialize form data when client changes
  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        clientStatus: client.clientStatus || client.client_status || 'Active Client',
        clientOf: client.clientOf || client.client_of || '',
        leadSource: client.leadSource || client.lead_source || '',
        servicesProvided: cleanArrayValue(client.servicesProvided || client.services_provided),
        brand: cleanArrayValue(client.brand),
        trimbleFormats: cleanArrayValue(client.trimbleFormats || client.trimble_formats),
        address: client.address || '',
        state: client.state || '',
        companyPhone: client.companyPhone || client.company_phone || '',
        reason: client.reason || '',
        levelOfDetail: client.levelOfDetail || client.level_of_detail || '',
        needsHandicapRamps: client.needsHandicapRamps ?? client.needs_handicap_ramps ?? false
      });
    }
  }, [client]);

  // Fetch contacts when modal opens
  useEffect(() => {
    const fetchContacts = async () => {
      if (client?.id && isOpen) {
        setLoadingContacts(true);
        try {
          const allContactsData = await ContactService.getAllContacts();
          const clientContacts = allContactsData.filter(contact => contact.clientId === client.id);
          setContacts(clientContacts || []);
        } catch (error) {
          console.error('Error fetching contacts:', error);
          setContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      }
    };

    fetchContacts();
  }, [client?.id, isOpen]);

  const handleEdit = () => {
    // Only allow edit if user has permission
    if (!canManageClients) {
      return;
    }
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    // Reset form data to original client data
    if (client) {
      setFormData({
        name: client.name || '',
        clientStatus: client.clientStatus || client.client_status || 'Active Client',
        clientOf: client.clientOf || client.client_of || '',
        leadSource: client.leadSource || client.lead_source || '',
        servicesProvided: cleanArrayValue(client.servicesProvided || client.services_provided),
        brand: cleanArrayValue(client.brand),
        trimbleFormats: cleanArrayValue(client.trimbleFormats || client.trimble_formats),
        address: client.address || '',
        state: client.state || '',
        companyPhone: client.companyPhone || client.company_phone || '',
        reason: client.reason || '',
        levelOfDetail: client.levelOfDetail || client.level_of_detail || '',
        needsHandicapRamps: client.needsHandicapRamps ?? client.needs_handicap_ramps ?? false
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (!formData.clientStatus) {
      newErrors.clientStatus = 'Status is required';
    }

    if (!formData.clientOf) {
      newErrors.clientOf = 'Client Of is required';
    }

    if (formData.companyPhone.trim()) {
      const cleaned = formData.companyPhone.replace(/\D/g, '');
      if (cleaned.length !== 10) {
        newErrors.companyPhone = 'Phone number must be 10 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Only allow save if user has permission
    if (!canManageClients) {
      setErrors({ submit: 'You do not have permission to edit clients.' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const updatedClient = await ClientService.updateClient(client.id, {
        name: formData.name,
        clientStatus: formData.clientStatus,
        clientOf: formData.clientOf,
        leadSource: formData.leadSource || null,
        servicesProvided: formData.servicesProvided.length > 0 ? formData.servicesProvided : null,
        brand: formData.brand.length > 0 ? formData.brand : null,
        trimbleFormats: formData.trimbleFormats.length > 0 ? formData.trimbleFormats : null,
        address: formData.address || null,
        state: formData.state || null,
        companyPhone: formData.companyPhone || null,
        reason: formData.reason || null,
        levelOfDetail: formData.levelOfDetail || null,
        needsHandicapRamps: formData.needsHandicapRamps
      });

      // Update formData with the saved values (already cleaned) so view mode shows the changes
      setFormData({
        name: updatedClient.name || '',
        clientStatus: updatedClient.clientStatus || 'Active Client',
        clientOf: updatedClient.clientOf || '',
        leadSource: updatedClient.leadSource || '',
        servicesProvided: cleanArrayValue(updatedClient.servicesProvided),
        brand: cleanArrayValue(updatedClient.brand),
        trimbleFormats: cleanArrayValue(updatedClient.trimbleFormats),
        address: updatedClient.address || '',
        state: updatedClient.state || '',
        companyPhone: updatedClient.companyPhone || '',
        reason: updatedClient.reason || '',
        levelOfDetail: updatedClient.levelOfDetail || '',
        needsHandicapRamps: updatedClient.needsHandicapRamps ?? false
      });

      setIsEditMode(false);
      setErrors({});

      if (onClientUpdated) {
        onClientUpdated(updatedClient);
      }
    } catch (error) {
      console.error('Error updating client:', error);
      setErrors({ submit: 'Failed to update client. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, companyPhone: formatted }));
    if (errors.companyPhone) {
      setErrors(prev => ({ ...prev, companyPhone: undefined }));
    }
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const servicesProvided = prev.servicesProvided.includes(service)
        ? prev.servicesProvided.filter(s => s !== service)
        : [...prev.servicesProvided, service];
      return { ...prev, servicesProvided };
    });
  };

  const handleBrandToggle = (brandOption) => {
    setFormData(prev => {
      const brand = prev.brand.includes(brandOption)
        ? prev.brand.filter(b => b !== brandOption)
        : [...prev.brand, brandOption];
      return { ...prev, brand };
    });
  };

  const handleTrimbleFormatToggle = (format) => {
    setFormData(prev => {
      const trimbleFormats = prev.trimbleFormats.includes(format)
        ? prev.trimbleFormats.filter(f => f !== format)
        : [...prev.trimbleFormats, format];
      return { ...prev, trimbleFormats };
    });
  };

  const handleDelete = () => {
    // Only allow delete if user has permission
    if (!canManageClients) {
      setErrors({ submit: 'You do not have permission to delete clients.' });
      return;
    }
    if (!client?.id) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    // Only allow delete if user has permission
    if (!canManageClients) {
      setErrors({ submit: 'You do not have permission to delete clients.' });
      setShowDeleteConfirm(false);
      return;
    }
    if (!client?.id) return;

    setLoading(true);
    try {
      await ClientService.deleteClient(client.id);

      // Close both modals and trigger refresh
      setShowDeleteConfirm(false);
      if (onClientUpdated) {
        onClientUpdated(null); // Signal that client was deleted
      }
      onClose();
    } catch (error) {
      console.error('Error deleting client:', error);
      setErrors({ submit: error.message || 'Failed to delete client. Please try again.' });
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneDisplay = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
    setShowContactModal(true);
  };

  const handleContactUpdate = async (updatedContact) => {
    // Refresh the contacts list
    if (client?.id) {
      try {
        const allContactsData = await ContactService.getAllContacts();
        const clientContacts = allContactsData.filter(contact => contact.clientId === client.id);
        setContacts(clientContacts || []);
      } catch (error) {
        console.error('Error refreshing contacts:', error);
      }
    }
  };

  if (!client) return null;

  return (
    <>
      <Modal trackAs="ClientDetailsModal" isOpen={isOpen} onClose={onClose} size="lg">
        {/* Custom Header with Icon and Title */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'var(--cta-blue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Building2 size={28} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formData.name || 'Unknown Client'}
              </h1>
              <p
                style={{
                  margin: 0,
                  marginTop: 'var(--space-1)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${formData.clientStatus} - ${formData.clientOf || 'Not assigned'}`}
              </p>
            </div>
          </div>
        </div>

        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Two column layout for fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Basic Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Basic Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Client Name */}
                {isEditMode ? (
                  <Input
                    label="Client Name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter client name"
                    error={errors.name}
                    required
                    icon={<Building2 size={16} />}
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Building2 size={14} />
                      Client Name
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {formData.name || 'Not provided'}
                    </div>
                  </div>
                )}

                {/* Client Status */}
                {isEditMode ? (
                  <Dropdown
                    label="Status"
                    options={clientStatusOptions}
                    value={formData.clientStatus}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, clientStatus: value }));
                      if (errors.clientStatus) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.clientStatus;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Select status"
                    error={errors.clientStatus}
                    required
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Tag size={14} />
                      Status
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {formData.clientStatus || 'Not provided'}
                    </div>
                  </div>
                )}

                {/* Client Of */}
                {isEditMode ? (
                  <Dropdown
                    label="Client Of"
                    options={clientOfOptions}
                    value={formData.clientOf}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, clientOf: value }));
                      if (errors.clientOf) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.clientOf;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Select..."
                    error={errors.clientOf}
                    required
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Briefcase size={14} />
                      Client Of
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {formData.clientOf || 'Not assigned'}
                    </div>
                  </div>
                )}

                {/* Address */}
                {(formData.address || isEditMode) && (
                  isEditMode ? (
                    <div>
                      <label
                        htmlFor="address"
                        style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          color: 'var(--gray-300)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                        }}
                      >
                        Address
                      </label>
                      <textarea
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Enter client address"
                        style={{
                          width: '100%',
                          padding: 'var(--space-3)',
                          backgroundColor: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-200)',
                          fontSize: 'var(--text-sm)',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          transition: 'border-color var(--transition-fast)',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--cta-blue)';
                          e.target.style.outline = 'none';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--gray-600)';
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <MapPin size={14} />
                        Address
                      </label>
                      <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                        {formData.address || 'Not provided'}
                      </div>
                    </div>
                  )
                )}

                {/* State */}
                {(formData.state || isEditMode) && (
                  isEditMode ? (
                    <Input
                      label="State"
                      name="state"
                      type="text"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="e.g., CA"
                      icon={<MapPin size={16} />}
                    />
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <MapPin size={14} />
                        State
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formData.state || 'Not provided'}
                      </div>
                    </div>
                  )
                )}

                {/* Company Phone */}
                {(formData.companyPhone || isEditMode) && (
                  isEditMode ? (
                    <Input
                      label="Company Phone"
                      name="companyPhone"
                      type="tel"
                      value={formData.companyPhone}
                      onChange={handlePhoneChange}
                      placeholder="(123) 456-7890"
                      error={errors.companyPhone}
                      icon={<Phone size={16} />}
                    />
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Phone size={14} />
                        Company Phone
                      </label>
                      <a
                        href={`tel:${formData.companyPhone}`}
                        style={{ color: 'var(--cta-blue)', fontSize: 'var(--text-base)', textDecoration: 'none', fontWeight: 'var(--font-medium)' }}
                        onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                      >
                        {formatPhoneDisplay(formData.companyPhone)}
                      </a>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Right Column: Service & Model Details */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Service Details
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Level of Detail */}
                {isEditMode ? (
                  <Dropdown
                    label="Level of Detail"
                    options={levelOfDetailOptions}
                    value={formData.levelOfDetail}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, levelOfDetail: value }));
                    }}
                    placeholder="Select level..."
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Layers size={14} />
                      Level of Detail
                    </label>
                    <div style={{ color: formData.levelOfDetail ? 'var(--gray-100)' : 'var(--gray-500)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {formData.levelOfDetail || 'Not set'}
                    </div>
                  </div>
                )}

                {/* Needs Handicap Ramps */}
                {isEditMode ? (
                  <div
                    style={{
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <Checkbox
                      label="Needs Handicap Ramps"
                      checked={formData.needsHandicapRamps}
                      onChange={() => {
                        setFormData((prev) => ({ ...prev, needsHandicapRamps: !prev.needsHandicapRamps }));
                      }}
                    />
                  </div>
                ) : (
                  formData.needsHandicapRamps && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Accessibility size={14} />
                        Handicap Ramps
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        Yes
                      </div>
                    </div>
                  )
                )}

                {/* Brand */}
                {(formData.brand.length > 0 || isEditMode) && (
                  isEditMode ? (
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          color: 'var(--gray-300)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                        }}
                      >
                        Brand
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {fieldOptions.brand.map((brandOption) => (
                          <Checkbox
                            key={brandOption}
                            label={brandOption}
                            checked={formData.brand.includes(brandOption)}
                            onChange={() => handleBrandToggle(brandOption)}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Brand
                      </label>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        {formData.brand.map((brandItem, idx) => (
                          <Badge key={idx} variant="info" size="sm">
                            {brandItem}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* Trimble Formats - only show in edit mode if Trimble is selected */}
                {(formData.trimbleFormats.length > 0 || (isEditMode && formData.brand.includes('Trimble'))) && (
                  isEditMode ? (
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          color: 'var(--gray-300)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                        }}
                      >
                        Trimble Formats
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {fieldOptions.trimbleFormats.map((format) => (
                          <Checkbox
                            key={format}
                            label={format}
                            checked={formData.trimbleFormats.includes(format)}
                            onChange={() => handleTrimbleFormatToggle(format)}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Trimble Formats
                      </label>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        {formData.trimbleFormats.map((format, idx) => (
                          <Badge key={idx} variant="info" size="sm">
                            {format}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* Services Provided */}
                {isEditMode ? (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--gray-300)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                      }}
                    >
                      Services Provided
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      {fieldOptions.servicesProvided.map((service) => (
                        <Checkbox
                          key={service}
                          label={service}
                          checked={formData.servicesProvided.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  formData.servicesProvided.length > 0 && (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Services Provided
                      </label>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        {formData.servicesProvided.map((service, idx) => (
                          <Badge key={idx} variant="info" size="sm">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* Lead Source */}
                {(formData.leadSource || isEditMode) && (
                  isEditMode ? (
                    <Dropdown
                      label="Lead Source"
                      options={leadSourceOptions}
                      value={formData.leadSource}
                      onChange={(value) => {
                        setFormData((prev) => ({ ...prev, leadSource: value }));
                      }}
                      placeholder="Select lead source..."
                    />
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Lead Source
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {formData.leadSource || 'Not provided'}
                      </div>
                    </div>
                  )
                )}

                {/* Reason */}
                {(formData.reason || isEditMode) && (
                  isEditMode ? (
                    <Input
                      label="Reason"
                      name="reason"
                      type="text"
                      value={formData.reason}
                      onChange={handleChange}
                      placeholder="Enter reason"
                    />
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Reason
                      </label>
                      <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                        {formData.reason || 'Not provided'}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Full Width: Contacts Section */}
          {!isEditMode && (
            <div style={{ marginTop: 'var(--space-6)', borderTop: '1px solid var(--gray-700)', paddingTop: 'var(--space-6)' }}>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Contacts ({contacts.length})
              </h2>

              {loadingContacts ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-8)',
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid var(--gray-600)',
                    borderTopColor: 'var(--cta-blue)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                </div>
              ) : contacts.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-6)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                  textAlign: 'center',
                }}>
                  <User size={24} style={{ color: 'var(--gray-500)' }} />
                  <p style={{
                    margin: 0,
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                  }}>
                    No contacts
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: 'var(--space-3)',
                  }}
                >
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => handleContactClick(contact)}
                      style={{
                        background: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-3)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--gray-500)';
                        e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--gray-700)';
                        e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
                      }}
                    >
                      {/* Contact Name & Role */}
                      <div style={{ marginBottom: 'var(--space-2)' }}>
                        <div
                          style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-semibold)',
                            color: 'var(--gray-100)',
                            marginBottom: contact.role ? 'var(--space-1)' : 0,
                          }}
                        >
                          {contact.name || 'Unknown Contact'}
                        </div>
                        {contact.role && (
                          <Badge variant="info" size="sm">
                            {contact.role}
                          </Badge>
                        )}
                      </div>

                      {/* Contact Details */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {contact.phoneNumber && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Phone size={12} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                            <a
                              href={`tel:${contact.phoneNumber}`}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--cta-blue)',
                                textDecoration: 'none',
                              }}
                              onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                              onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                            >
                              {formatPhoneDisplay(contact.phoneNumber)}
                            </a>
                          </div>
                        )}

                        {contact.email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Mail size={12} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                            <a
                              href={`mailto:${contact.email}`}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--cta-blue)',
                                textDecoration: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                              onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                            >
                              {contact.email}
                            </a>
                          </div>
                        )}

                        {contact.billing && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <DollarSign size={12} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--success)' }}>
                              Billing Contact
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModalBody>

        {/* Sticky Footer with Actions */}
        <ModalFooter>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {isEditMode ? (
              <>
                {/* Left: Delete Button */}
                <Button variant="danger" size="md" onClick={handleDelete} disabled={loading} icon={<Trash2 size={16} />}>
                  Delete Client
                </Button>

                {/* Right: Cancel & Save */}
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <Button variant="ghost" size="md" onClick={handleCancelEdit} disabled={loading}>
                    Cancel
                  </Button>
                  <Button variant="primary" size="md" onClick={handleSave} loading={loading} icon={<Check size={16} />}>
                    Save Changes
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Empty left side for balance */}
                <div></div>

                {/* Right: Edit Button - only show if user has permission */}
                {canManageClients ? (
                  <Button variant="secondary" size="md" onClick={handleEdit} icon={<Edit size={16} />}>
                    Edit Client
                  </Button>
                ) : (
                  <div style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-400)',
                    fontStyle: 'italic'
                  }}>
                    You don't have permission to edit clients
                  </div>
                )}
              </>
            )}
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Client"
        message={`Are you sure you want to delete ${client.name}? This action cannot be undone${client.contactCount > 0 ? ` and there are ${client.contactCount} contacts linked to this client.` : '.'}`}
        confirmText="Delete Client"
        cancelText="Cancel"
        variant="danger"
        loading={loading}
      />

      {/* Contact Details Modal */}
      {showContactModal && selectedContact && (
        <ContactDetailsModal
          contact={selectedContact}
          isOpen={showContactModal}
          onClose={() => {
            setShowContactModal(false);
            setSelectedContact(null);
          }}
          onContactUpdated={handleContactUpdate}
        />
      )}
    </>
  );
};

export default ClientDetailsModal;
